// src/components/MomentList.tsx - med optimerade animationer
import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { Search, FileText, Users, Info, ChevronRight } from "lucide-react";
import ProcessNumber from "./ProcessNumber";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Moment {
  title: string;
  hierarchicalNumber?: string;
  description?: string;
  documents?: Array<{title: string, url: string}>;
  participants?: string[];
  viktigt?: string;
}

interface MomentListProps {
  moments: Moment[];
  activeMoment: string;
  onSelectMoment: (title: string) => void;
  highlightedItems?: Set<string>;
}

// Konstanter för animationer
const listAnimationConfig = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: {
    type: "spring",
    stiffness: 500,
    damping: 30,
    mass: 0.8
  }
};

const itemAnimationConfig = {
  initial: { opacity: 0, x: -5 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -5 },
  transition: {
    type: "spring",
    stiffness: 500,
    damping: 30,
    mass: 0.8
  }
};

// En enklare memoizerad MomentItem utan forwardRef
const MomentItem = React.memo(({ 
  moment, 
  isActive, 
  isHighlighted, 
  onClick, 
  viewStyle,
  index
}: { 
  moment: Moment; 
  isActive: boolean; 
  isHighlighted: boolean; 
  onClick: () => void; 
  viewStyle: 'compact' | 'detailed';
  index: number;
}) => {
  // Använd ref för att kunna scrolla
  const itemRef = useRef<HTMLDivElement>(null);
  
  // Scroll till detta element om det är aktivt
  useEffect(() => {
    if (isActive && itemRef.current) {
      // Mer subtil scrollning med timeout för att undvika ryckighet
      const timer = setTimeout(() => {
        itemRef.current?.scrollIntoView({ 
          behavior: "smooth", 
          block: "nearest" 
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  // Staggar-delay baserat på index
  const staggerDelay = index * 0.03;

  // Compact view - Använd en div istället för motion.button
  if (viewStyle === 'compact') {
    return (
      <div ref={itemRef}>
        <motion.div
          layout
          layoutId={`moment-compact-${moment.title}`}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 400,
            damping: 30,
            delay: staggerDelay
          }}
          className={cn(
            "w-full text-left p-3 rounded-lg transition-all will-change-transform cursor-pointer",
            isActive && isHighlighted
              ? "bg-orange-300 text-gray-900 highlighted-border-active"
              : isActive
              ? "bg-orange-300 text-gray-900"
              : isHighlighted
              ? "bg-[#FFF4E3] text-gray-900 hover:bg-[#FFF4E3]/80 highlighted-border"
              : "bg-[#FFF4E3] text-gray-900 hover:bg-[#FFF4E3]/80"
          )}
          onClick={onClick}
          id={`moment-item-${moment.title.replace(/\s+/g, '-').toLowerCase()}`}
          data-testid={`moment-item-${isActive ? 'active' : 'inactive'}`}
          data-highlighted={isHighlighted ? "true" : "false"}
        >
          <div className="flex items-center gap-2">
          <ProcessNumber
            number={moment.hierarchicalNumber || ""}
            active={isActive}
            viewStyle="compact"
/>
            <span className={cn(
              "leading-tight text-base",
              isActive 
                ? "font-medium" 
                : isHighlighted
                ? "font-medium text-[#CE5325]"
                : "font-normal"
            )}>
              {moment.title}
            </span>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Detailed view - Använd en div istället för motion.button
  return (
    <div ref={itemRef}>
      <motion.div
        layout
        layoutId={`moment-detailed-${moment.title}`}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ 
          type: "spring",
          stiffness: 400,
          damping: 30,
          delay: staggerDelay
        }}
        className={cn(
          "w-full text-left rounded-lg overflow-hidden border transition-all will-change-transform cursor-pointer",
          isActive && isHighlighted
            ? "bg-orange-300/10 border-orange-300/30 highlighted-border-active"
            : isActive
            ? "bg-orange-300/10 border-orange-300/30" 
            : isHighlighted
            ? "bg-gray-800/50 border-[rgb(180,237,185)]/50 hover:bg-gray-700/40 highlighted-border"
            : "bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/40"
        )}
        onClick={onClick}
        id={`moment-item-detailed-${moment.title.replace(/\s+/g, '-').toLowerCase()}`}
        data-testid={`moment-item-detailed-${isActive ? 'active' : 'inactive'}`}
        data-highlighted={isHighlighted ? "true" : "false"}
      >
        {/* Färgad toppkant */}
        <div className={`h-1 ${
          isActive 
            ? 'bg-orange-300/80' 
            : isHighlighted 
            ? 'bg-[rgb(180,237,185)]' 
            : 'bg-orange-300/80'
        }`} />
        
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
          <ProcessNumber
            number={moment.hierarchicalNumber || ""}
            active={isActive}
            viewStyle="detailed"
/>
            <span className={cn(
              "leading-tight text-base",
              isActive 
                ? "font-medium text-orange-300" 
                : isHighlighted
                ? "font-medium text-[rgb(180,237,185)]"
                : "font-normal text-white"
            )}>
              {moment.title}
            </span>
          </div>
          
          {/* Kort beskrivning om den finns */}
          {moment.description && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-xs text-gray-400 ml-8 mb-2 line-clamp-2"
            >
              {moment.description}
            </motion.p>
          )}
          
          {/* Indikatorer för innehåll */}
          <motion.div 
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex gap-2 ml-8"
          >
            {moment.description && (
              <div className="w-5 h-5 rounded-full bg-orange-900/30 flex items-center justify-center" title="Har beskrivning">
                <Info className="h-3 w-3 text-orange-300" />
              </div>
            )}
            {moment.documents && moment.documents.length > 0 && (
              <div className="w-5 h-5 rounded-full bg-blue-900/30 flex items-center justify-center" title={`${moment.documents.length} dokument`}>
                <FileText className="h-3 w-3 text-blue-300" />
              </div>
            )}
            {moment.participants && moment.participants.length > 0 && (
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center" title={`${moment.participants.length} deltagare`}>
                <Users className="h-3 w-3 text-green-300" />
              </div>
            )}
            {moment.viktigt && (
              <span className="animate-pulse text-xs font-medium text-red-400">
                Viktigt
              </span>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
});

// Sätt displayName för bättre debugging
MomentItem.displayName = "MomentItem";

const MomentList: React.FC<MomentListProps> = ({ 
  moments, 
  activeMoment, 
  onSelectMoment,
  highlightedItems = new Set()
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewStyle, setViewStyle] = useState<'compact' | 'detailed'>('compact');
  const [visibleCount, setVisibleCount] = useState(10); // För progressiv rendering
  const listRef = useRef<HTMLDivElement>(null);
  
  // Filtrera moment baserat på sökning
  const filteredMoments = searchTerm 
    ? moments.filter(moment => 
        moment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (moment.description && moment.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : moments;

  // Progressiv rendering för långa listor
  useEffect(() => {
    setVisibleCount(Math.min(15, filteredMoments.length));
    
    if (filteredMoments.length > 15) {
      const timer = setTimeout(() => {
        setVisibleCount(filteredMoments.length);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filteredMoments.length, searchTerm]);

  // Hantera visningsstilsändring med smidig överföring
  const handleViewStyleChange = useCallback((newStyle: 'compact' | 'detailed') => {
    setViewStyle(newStyle);
  }, []);

  // Memoizerad moment-selektering för bättre prestanda
  const handleSelectMoment = useCallback((title: string) => {
    onSelectMoment(title);
  }, [onSelectMoment]);

  return (
    <Card className="h-auto bg-gray-800/20 border-gray-700/50 backdrop-blur-md" id="moment-list-card">
      <CardContent className="p-4" id="moment-list-content">
        {/* Header med rubrik, sökning och visningskontroller */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-between items-center mb-4"
          id="moment-list-header"
        >
          <h3 className="text-lg font-medium text-white flex items-center gap-2" id="moment-list-title">
            <span>Moment</span>
            {moments.length > 0 && (
              <span className="text-xs text-gray-400" id="moment-count">
                ({filteredMoments.length}/{moments.length})
              </span>
            )}
          </h3>
          
          <div className="flex gap-2" id="moment-list-controls">
            {/* Sökfält */}
            {moments.length > 3 && (
              <motion.div 
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className="relative"
                id="moment-search-container"
              >
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Sök moment..."
                  className="w-32 bg-gray-800/50 border border-gray-700 rounded-lg py-1 pl-7 pr-2 text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-300/50"
                  id="moment-search-input"
                />
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
              </motion.div>
            )}
            
            {/* Växla mellan kompakt och detaljerad vy */}
            <div className="bg-gray-800/50 rounded-lg flex p-1 border border-gray-700" id="view-style-toggles">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleViewStyleChange('compact')}
                className={`p-1 rounded ${viewStyle === 'compact' ? 'bg-orange-300/20 text-orange-300' : 'text-gray-400 hover:text-white'}`}
                title="Kompakt vy"
                id="compact-view-button"
              >
                <ChevronRight className="h-3 w-3" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleViewStyleChange('detailed')}
                className={`p-1 rounded ${viewStyle === 'detailed' ? 'bg-orange-300/20 text-orange-300' : 'text-gray-400 hover:text-white'}`}
                title="Detaljerad vy"
                id="detailed-view-button"
              >
                <Info className="h-3 w-3" />
              </motion.button>
            </div>
          </div>
        </motion.div>
        
        {/* Lista på moment - med auto-height och dold scrollbar */}
        <div 
          ref={listRef}
          className="space-y-2 max-h-[calc(100vh-240px)] overflow-y-auto no-scrollbar pr-1"
          id="moment-items-container"
        >
          {filteredMoments.length > 0 ? (
            filteredMoments.slice(0, visibleCount).map((moment, idx) => {
              const isHighlighted = highlightedItems && highlightedItems.has(moment.title);
              
              return (
                <MomentItem
                  key={moment.title}
                  moment={moment}
                  isActive={activeMoment === moment.title}
                  isHighlighted={isHighlighted}
                  onClick={() => handleSelectMoment(moment.title)}
                  viewStyle={viewStyle}
                  index={idx}
                />
              );
            })
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-4 text-gray-400 bg-gray-800/30 rounded-lg border border-gray-700/30"
              id="empty-moments-message"
            >
              {searchTerm ? (
                <>
                  <p>Inga moment matchar sökningen</p>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSearchTerm("")}
                    className="text-orange-300 text-sm hover:underline mt-1"
                    id="clear-search-button"
                  >
                    Rensa sökning
                  </motion.button>
                </>
              ) : (
                <p>Inga moment tillgängliga</p>
              )}
            </motion.div>
          )}
          
          {/* Laddningsindikator för progressiv rendering */}
          {visibleCount < filteredMoments.length && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-2 text-gray-400 text-sm"
              id="loading-more-indicator"
            >
              Laddar fler moment...
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(MomentList);